import type { ResultOf } from "@graphql-typed-document-node/core";
import type * as Core from "@zeller-public/payments-sdk-core";
import { useEffect } from "react";

import {
	TransactionStatus,
	TransactionType,
} from "../../../schema.dev-sdk-api-appsync.generated";
import { logger } from "../../shared/logger";
import * as transport from "../../transport";
import { isUuid } from "./shared/is-uuid";
import { Route } from "./shared/route";
import { useApiClient } from "./shared/use-api-client";
import { getTransaction } from "./terminal-get-transactions/get-transaction";
import { getTransactions } from "./terminal-get-transactions/get-transactions";

const resolve = transport.Resolve<"Terminal_getTransactions">();

export default Route(
	{ query: "Terminal_getTransactions", interactive: false },
	function ({ query: { reference }, headers: { deviceId } }) {
		const apiClient = useApiClient();

		useEffect(() => {
			void (async () => {
				logger.debug("transactions: request", { deviceId, reference });

				if (!deviceId) {
					return void resolve.Error("Setup Required", {
						message: "Not configured.",
					});
				}

				const transactionsResponse = await getTransactions({
					apiClient,
					deviceId,
					reference,
				});

				if (transactionsResponse instanceof Error) {
					return void resolve.Error("Operation Failure", {
						message: transactionsResponse.message,
					});
				}

				const response = mapTransactionsToResponse(
					transactionsResponse.transactions,
				);

				if (response.length) {
					return void resolve.Ok(response);
				}

				if (reference && isUuid(reference)) {
					const transactionResponse = await getTransaction({
						apiClient,
						deviceId,
						transactionId: reference,
					});

					if (transactionResponse instanceof Error) {
						return void resolve.Error("Operation Failure", {
							message: transactionResponse.message,
						});
					}

					const response = mapTransactionsToResponse([transactionResponse]);
					return void resolve.Ok(response);
				}

				return void resolve.Ok([]);
			})();
		}, [apiClient, deviceId, reference]);

		return null;
	},
);

type RequestTransaction = ResultOf<
	typeof getTransactions.gql.getTransactions
>["getTransactions"]["transactions"][number];

type FilteredRequestTransaction = RequestTransaction & {
	type: TransactionType.PURCHASE | TransactionType.REFUND;
};

function isPurchaseOrRefundType(
	t: RequestTransaction,
): t is FilteredRequestTransaction {
	return (
		t.type === TransactionType.PURCHASE || t.type === TransactionType.REFUND
	);
}

function mapTransactionsToResponse(
	transactions: RequestTransaction[],
): Core.Protocol.Transaction[] {
	// request should only return purchase or refund transactions but
	// filtering to for completeness and to satisfy the type checker
	const filteredTransactions = transactions.filter(isPurchaseOrRefundType);

	return filteredTransactions.map<Core.Protocol.Transaction>((t) => ({
		// MANDATORY
		$type: t.status === TransactionStatus.APPROVED ? "Approved" : "Declined",
		amount: t.amount,
		caid: t.caid ?? "",
		currency: t.currency ?? "AUD",
		responseCode: t.responseCode ?? "",
		responseText: t.responseDescription ?? "",

		// REMAP REQUIRED. QUERY RESPONSE RETURNS: APPROVED,
		// DECLINED, DELETED, PROCESSING. -- BUT SCHEME ONLY
		// ACCEPTS: APPROVED, DECLINED, FAILED.
		status:
			t.status === TransactionStatus.APPROVED
				? "APPROVED"
				: t.status === TransactionStatus.DECLINED
					? "DECLINED"
					: "FAILED",
		timestampLocal: t.timestamp ?? new Date().toISOString(),
		transactionUuid: t.id,
		type: t.type,

		// OPTIONAL
		aid: t.emvAid ?? undefined,
		applicationName: t.emvAppName ?? undefined,
		approvalCode: t.approvalCode ?? undefined,
		bin: t.bin ?? undefined,
		catid: t.catid ?? undefined,
		emvTagsPrint: t.emvTagsPrint ?? undefined,
		externalReference: t.externalReference ?? undefined,
		panMasked: t.maskedPan ?? undefined,
		receiptLink: t.receiptLink ?? undefined,
		rrn: t.reference ?? undefined,
		scheme: t.scheme,
		surchargeAmount: t.surchargeAmount,
		taxAmount: (t.taxAmounts ?? []).reduce(
			(memo: number, { amount }: { amount: number }) => memo + amount,
			0,
		),
		tipAmount: t.tipAmount,

		// UNABLE TO MAP SO HARDCODED TO UNDEFINED
		receiptText: undefined,
		cvm: undefined,
		sessionUuid: undefined,
	}));
}
